import { createOrganizationInitialUserApi, useOrganizationDetail } from "@/api/services/admin/organization";
import { useProductContractsByOrganization } from "@/api/services/admin/product-contract";
import { IconSave } from "@/assets/icons/IconSave";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { OrganizationCreateSetupInitialUser } from "@/features/organization/components/organization-create-setup/OrganizationCreateSetupInitialUser";
import { OrganizationCreateSetupProductRegistration } from "@/features/organization/components/organization-create-setup/OrganizationCreateSetupProductRegistration";
import {
  createOrganizationInitialUserSchema,
  TCreateOrganizationInitialUserForm,
} from "@/features/organization/schema/createOrganizationInitialUserSchema";
import { useSorting } from "@/hooks/useSorting";
import { useToast } from "@/hooks/useToast";
import { useVisible } from "@/hooks/useVisible";
import { TApiSortsReq } from "@/types/api";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const OrganizationCreateSetupPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const skipBlockerRef = useRef(false);

  useEffect(() => {
    if (!location.state?.fromCreate) {
      navigate(AppRoutes.organizations, { replace: true });
    }
  }, [location.state, navigate]);

  const { sorts } = useSorting();
  const { data: organizationData } = useOrganizationDetail();
  const organization = organizationData?.data;

  const {
    data: productContractsData,
    isFetching,
    refetch,
  } = useProductContractsByOrganization(id, {
    sorts: sorts.length > 0 ? (sorts as TApiSortsReq[]) : undefined,
  });
  const productContracts = productContractsData?.data ?? [];

  const methods = useForm<TCreateOrganizationInitialUserForm>({
    defaultValues: {
      userName: "",
      email: "",
    },
    resolver: yupResolver(createOrganizationInitialUserSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control } = methods;
  const confirmModal = useVisible();
  const submitting = useVisible();

  const submitForm = async (data: TCreateOrganizationInitialUserForm) => {
    if (!id) return;
    try {
      await createOrganizationInitialUserApi(id, { email: data.email, name: data.userName });
      toast.success(API_SUCCESS_MESSAGE);
      skipBlockerRef.current = true;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_LIST] });
      navigate(AppRoutes.organizations);
    } catch (error) {
      showError(error);
    } finally {
      submitting.close();
    }
  };

  const onSubmit = async (data: TCreateOrganizationInitialUserForm) => {
    const hasTemporary = productContracts.some((c) => c.isTemporary);
    submitting.open();
    if (hasTemporary && productContracts.length > 0) {
      confirmModal.open();
      return;
    }
    await submitForm(data);
  };

  const handleConfirmSubmit = async () => {
    confirmModal.close();
    await submitForm(methods.getValues());
  };

  const handleBack = () => {
    navigate(AppRoutes.organizations);
  };

  const handleRefreshContracts = useCallback(() => {
    refetch();
  }, [refetch]);

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "各プロダクトの正式登録",
        content: (
          <OrganizationCreateSetupProductRegistration productContracts={productContracts} isLoading={isFetching} onRefresh={handleRefreshContracts} />
        ),
      },
      {
        title: "最初に登録するユーザー情報",
        content: <OrganizationCreateSetupInitialUser control={control} />,
      },
    ],
    [productContracts, isFetching, handleRefreshContracts, control],
  );

  return (
    <Form
      height="100%"
      methods={methods}
      onSubmit={onSubmit}
      schema={createOrganizationInitialUserSchema}
      forceBlock
      shouldBlock={({ currentLocation, nextLocation }) => {
        if (skipBlockerRef.current) return false;
        if (currentLocation.pathname === nextLocation.pathname) return false;
        return true;
      }}
    >
      <Stack gap={3}>
        <Stack gap={2}>
          <Typography variant="head20Bold" color={text.primary}>
            新規組織の登録 2/2
          </Typography>
          <Typography variant="body14" color={text.secondary}>
            各プロダクトは仮登録状態であるため、それぞれ個別に正式な登録を進めてください。併せて最初に登録するお客様のユーザー情報も入力してください。
          </Typography>
        </Stack>

        <MenuNavigate
          menuPrefix={
            <Typography
              variant="body14Bold"
              color={text.primary}
              px={2}
              pb={1}
              sx={{
                wordBreak: "break-word",
              }}
            >
              {organization?.name ?? ""}
            </Typography>
          }
          contents={contents}
          contentAffix={
            <StickyFooter>
              <Stack width="100%" justifyContent="space-between" flexDirection="row">
                <Button size="sm" variant="ghost" onClick={handleBack}>
                  戻る
                </Button>
                <Button
                  size="sm"
                  type="submit"
                  isLoading={submitting.visible}
                  sx={{
                    gap: 1,
                  }}
                >
                  <IconSave color="currentColor" />
                  ユーザーを保存してメール送信
                </Button>
              </Stack>
            </StickyFooter>
          }
        />
      </Stack>

      <ConfirmSaveModal title="確認" open={confirmModal.visible} onClose={confirmModal.close} onOk={handleConfirmSubmit}>
        <Typography variant="body14">仮登録状態のプロダクトがありますが、このまま続行しますか？</Typography>
      </ConfirmSaveModal>
    </Form>
  );
};

export default OrganizationCreateSetupPage;
